import { FieldConfig } from '@lbcamden/lib-react';

import { ChildForm, ChildFormProps } from './ChildForm';
import { NumberedLabelContainer } from './common.styled';
import { ConditionalCheckboxGroup, ConditionalCheckboxGroupProps } from './ConditionalFormInput';
import { ConditionalChildForm } from './ConditionalFormInput/ConditionalChildForm';
import {
  ConditionalTextArea,
  ConditionalTextAreaProps,
} from './ConditionalFormInput/ConditionalTextArea';

type CustomRenderFn<TValue> = NonNullable<FieldConfig<TValue>['customRenderFn']>;

type ConditionalCheckboxGroupRenderFnOpts = Pick<
  ConditionalCheckboxGroupProps,
  'yesLabel' | 'noLabel' | 'nestedContent' | 'options'
>;

type ConditionalTextAreaRenderFnOpts = Pick<
  ConditionalTextAreaProps,
  'yesLabel' | 'noLabel' | 'textAreaLabel'
>;

type ChildFormRenderFnOpts<TData extends object> = Pick<ChildFormProps<TData>, 'schema' | 'config'>;

export const numberedLabel = (number: number, label: string) => (
  <NumberedLabelContainer>
    <span>{number}.</span>
    <span className="text">{label}</span>
  </NumberedLabelContainer>
);

export const getConditionalCheckboxGroupRenderFn =
  <TValue extends false | string[]>(
    opts: ConditionalCheckboxGroupRenderFnOpts,
  ): CustomRenderFn<TValue> =>
  (value, onChange, { key, readOnly }, errorDetails) => (
    <ConditionalCheckboxGroup
      {...opts}
      id={key}
      value={value}
      disabled={readOnly}
      errorOnEmptyCustomValues={!!errorDetails?.length}
      onChange={onChange}
    />
  );

export const getConditionalTextAreaRenderFn =
  <TValue extends false | string>(opts: ConditionalTextAreaRenderFnOpts): CustomRenderFn<TValue> =>
  (value, onChange, { key, readOnly }, errorDetails) => (
    <ConditionalTextArea
      {...opts}
      id={key}
      value={value}
      disabled={readOnly}
      errorOnEmpty={!!errorDetails?.length}
      onChange={onChange}
    />
  );

export const getChildFormRenderFn =
  <TData extends object>(opts: ChildFormRenderFnOpts<TData>): CustomRenderFn<TData> =>
  (value, onChange, { readOnly }, errorDetails) => (
    <ChildForm
      {...opts}
      value={value}
      disabled={readOnly}
      showErrors={!!errorDetails}
      onChange={onChange}
    />
  );

export const getConditionalChildFormRenderFn =
  <TData extends object, TValue extends false | TData = false | TData>(
    opts: ChildFormRenderFnOpts<TData>,
  ): CustomRenderFn<TValue> =>
  (value, onChange, { key, readOnly }, errorDetails) => (
    <ConditionalChildForm
      {...opts}
      id={key}
      value={value}
      disabled={readOnly}
      showErrors={!!errorDetails}
      onChange={onChange}
    />
  );
